import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { Recipe } from '@/types';
import { Heart, MessageSquare, Share2, Users, Clock, X, Globe, Lock, Send } from 'lucide-react';
import { formatDate, formatTime, shareRecipe } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import { Button } from './ui/button';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
}

export default function CommunityFeed() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [placeholderUrl, setPlaceholderUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState<{[key: string]: boolean}>({});

  useEffect(() => {
    if (user) {
      loadCommunityRecipes();
      loadPlaceholder();
    }
  }, [user]);

  useEffect(() => {
    if (selectedRecipe) {
      loadComments(selectedRecipe.id);
    }
  }, [selectedRecipe]);

  const loadPlaceholder = async () => {
    try {
      const { data: settings } = await supabase
        .from('app_settings')
        .select('recipe_placeholder_url')
        .single();
      
      setPlaceholderUrl(settings?.recipe_placeholder_url);
    } catch (error) {
      console.error('Error loading placeholder:', error);
    }
  };

  const loadCommunityRecipes = async () => {
    try {
      const { data, error } = await supabase
        .from('public_recipe_feed')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const transformedRecipes = data?.map(recipe => ({
        ...recipe,
        prepTime: recipe.prepTime || 0,
        cookTime: recipe.cookTime || 0,
        servings: recipe.servings || 1,
        ingredients: recipe.ingredients || [],
        instructions: recipe.instructions || [],
        tags: recipe.tags || [],
        profiles: {
          username: recipe.username,
          avatar_url: recipe.avatar_url
        }
      })) || [];

      setRecipes(transformedRecipes);
    } catch (error) {
      console.error('Error loading community recipes:', error);
      toast.error('Failed to load community recipes');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (recipeId: string) => {
    setLoadingComments(true);
    try {
      const { data, error } = await supabase
        .from('recipe_comments_with_profiles')
        .select('*')
        .eq('recipe_id', recipeId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLike = async (recipe: Recipe) => {
    try {
      if (recipe.is_liked) {
        const { error } = await supabase
          .from('recipe_likes')
          .delete()
          .eq('recipe_id', recipe.id)
          .eq('user_id', user?.id);

        if (error) throw error;
        toast.success('Recipe unliked');
      } else {
        const { error } = await supabase
          .from('recipe_likes')
          .insert({ recipe_id: recipe.id, user_id: user?.id });

        if (error) throw error;
        toast.success('Recipe liked!');
      }

      loadCommunityRecipes();
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update like');
    }
  };

  const handleShare = async (recipe: Recipe, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    setSharing({ ...sharing, [recipe.id]: true });
    try {
      const success = await shareRecipe(recipe);
      if (success) {
        toast.success('Recipe shared successfully!');
      }
    } catch (error) {
      console.error('Error sharing recipe:', error);
      toast.error('Failed to share recipe');
    } finally {
      setSharing({ ...sharing, [recipe.id]: false });
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRecipe || !newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const { error } = await supabase
        .from('recipe_comments')
        .insert({
          recipe_id: selectedRecipe.id,
          user_id: user?.id,
          content: newComment.trim()
        });

      if (error) throw error;

      setNewComment('');
      loadComments(selectedRecipe.id);
      toast.success('Comment added!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="text-gray-500">Loading community recipes...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {recipes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Recipes Yet</h3>
          <p className="text-gray-500 mb-6">
            Be the first to share a recipe with the community!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recipes.map((recipe) => (
            <div
              key={recipe.id}
              onClick={() => setSelectedRecipe(recipe)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="relative h-48">
                {recipe.imageUrl ? (
                  <img
                    src={recipe.imageUrl}
                    alt={recipe.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (placeholderUrl) {
                        target.src = placeholderUrl;
                        target.classList.add('opacity-50');
                      }
                    }}
                  />
                ) : placeholderUrl ? (
                  <img
                    src={placeholderUrl}
                    alt="Recipe placeholder"
                    className="w-full h-full object-cover opacity-50"
                  />
                ) : (
                  <div className="w-full h-full bg-primary-50 flex items-center justify-center">
                    <Users className="h-12 w-12 text-primary-300" />
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center">
                    {recipe.avatar_url ? (
                      <img
                        src={recipe.avatar_url}
                        alt={recipe.username}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <Users className="h-5 w-5 text-primary-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{recipe.username}</p>
                    <p className="text-sm text-gray-500">
                      {formatDate(new Date(recipe.created_at))}
                    </p>
                  </div>
                </div>

                <h3 className="text-lg font-semibold mb-3">{recipe.name}</h3>

                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary-500" />
                    <span>{formatTime(recipe.prepTime + recipe.cookTime)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary-500" />
                    <span>{recipe.servings} servings</span>
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLike(recipe);
                    }}
                    className={recipe.is_liked ? 'text-primary-600' : ''}
                  >
                    <Heart className={`h-4 w-4 mr-2 ${recipe.is_liked ? 'fill-current' : ''}`} />
                    <span>{recipe.like_count}</span>
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    <span>{recipe.comment_count}</span>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleShare(recipe, e)}
                    disabled={sharing[recipe.id]}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-2xl rounded-xl overflow-hidden relative">
              <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare(selectedRecipe);
                  }}
                  disabled={sharing[selectedRecipe.id]}
                  className="bg-white/90 hover:bg-white"
                >
                  <Share2 className="h-5 w-5" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedRecipe(null)}
                  className="bg-white/90 hover:bg-white"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {selectedRecipe.imageUrl && (
                <div className="relative h-64">
                  <img
                    src={selectedRecipe.imageUrl}
                    alt={selectedRecipe.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (placeholderUrl) {
                        target.src = placeholderUrl;
                        target.classList.add('opacity-50');
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <h2 className="absolute bottom-4 left-4 right-4 text-2xl font-semibold text-white">
                    {selectedRecipe.name}
                  </h2>
                </div>
              )}

              <div className="p-6">
                {!selectedRecipe.imageUrl && (
                  <h2 className="text-2xl font-semibold mb-4">{selectedRecipe.name}</h2>
                )}

                <div className="flex items-center gap-6 text-sm text-gray-600 mb-6">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary-500" />
                    <span>
                      Prep: {formatTime(selectedRecipe.prepTime)}
                      <span className="mx-1">•</span>
                      Cook: {formatTime(selectedRecipe.cookTime)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary-500" />
                    <span>{selectedRecipe.servings} servings</span>
                  </div>
                </div>

                <div className="space-y-6">
                  <section>
                    <h3 className="text-lg font-medium mb-3">Ingredients</h3>
                    <ul className="space-y-2">
                      {selectedRecipe.ingredients.map((ingredient, index) => (
                        <li key={index} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg">
                          <span className="w-16 text-sm text-gray-600">
                            {ingredient.amount} {ingredient.unit}
                          </span>
                          <span>{ingredient.name}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-medium mb-3">Instructions</h3>
                    <ol className="space-y-4">
                      {selectedRecipe.instructions.map((instruction, index) => (
                        <li key={index} className="flex gap-4">
                          <span className="flex-shrink-0 w-6 h-6 bg-primary-50 text-primary-600 rounded-full flex items-center justify-center text-sm">
                            {index + 1}
                          </span>
                          <span className="flex-1">{instruction}</span>
                        </li>
                      ))}
                    </ol>
                  </section>

                  <section>
                    <h3 className="text-lg font-medium mb-3">Comments</h3>
                    {loadingComments ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {comments.map((comment) => (
                          <div key={comment.id} className="flex gap-3">
                            <div className="flex-shrink-0">
                              {comment.avatar_url ? (
                                <img
                                  src={comment.avatar_url}
                                  alt={comment.username}
                                  className="w-8 h-8 rounded-full"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-primary-50 rounded-full flex items-center justify-center">
                                  <span className="text-primary-600 text-sm font-medium">
                                    {comment.username[0].toUpperCase()}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="font-medium text-sm">{comment.username}</p>
                                <p className="text-gray-700">{comment.content}</p>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDate(new Date(comment.created_at))}
                              </p>
                            </div>
                          </div>
                        ))}

                        <form onSubmit={handleSubmitComment} className="mt-4">
                          <div className="flex gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-primary-50 rounded-full flex items-center justify-center">
                                <span className="text-primary-600 text-sm font-medium">
                                  {user?.user_metadata?.username?.[0]?.toUpperCase() || 'U'}
                                </span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <textarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Add a comment..."
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                rows={2}
                              />
                              <div className="mt-2 flex justify-end">
                                <Button
                                  type="submit"
                                  disabled={!newComment.trim() || submittingComment}
                                >
                                  {submittingComment ? 'Posting...' : 'Post'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}